'use client'

import {
    ChatFolder,
    createChatFolder,
    moveChatToFolder,
    removeChatFromFolder,
} from '@/lib/api/atendimento/chat-folders'
import { CHAT_READ_CHANGED_EVENT, markChatUnread } from '@/lib/api/atendimento/chat'
import { useState } from 'react'
import {
    MdArrowBack,
    MdCreateNewFolder,
    MdDriveFileMoveOutline,
    MdCheck,
    MdExpandMore,
    MdFolderOff,
    MdMarkChatUnread,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface ChatActionsMenuProps {
    chatId: number
    folders: ChatFolder[]
    currentFolderId?: number | null
    /** Disparado após mover, remover, criar pasta ou marcar como não lido. */
    onChanged?: () => void
    /** Classe extra para o botão (tamanho/cor). */
    buttonClassName?: string
    iconClassName?: string
    /**
     * Oferece "marcar como não lido". Só faz sentido na LISTAGEM: dentro da conversa
     * aberta, a leitura é zerada de novo no instante seguinte.
     */
    showMarkUnread?: boolean
    /**
     * A conversa JÁ está não lida: o item não aparece, porque não mudaria nada. O menu
     * abre direto nas pastas, mas o botão continua o mesmo — ícone diferente por linha
     * deixaria a lista desalinhada.
     */
    alreadyUnread?: boolean
}

/**
 * Ações da conversa: mover para pasta e marcar como não lido.
 *
 * Marcar como não lido é o "deixo para depois" de quem varre a fila — abrir para ler já
 * apagava o sinal, e a conversa sumia da lista de quem ainda precisa responder.
 *
 * Com as duas ações o menu ganha um primeiro nível; as pastas viram o segundo passo.
 * Sem elas (dentro da conversa), abre direto nas pastas, como antes.
 */
export default function ChatActionsMenu({
    chatId,
    folders,
    currentFolderId,
    onChanged,
    buttonClassName = 'btn btn-ghost btn-xs btn-circle',
    iconClassName = 'text-base',
    showMarkUnread = false,
    alreadyUnread = false,
}: ChatActionsMenuProps) {
    const [busy, setBusy] = useState(false)
    const [creating, setCreating] = useState(false)
    const [newName, setNewName] = useState('')
    // Com uma ação só, a raiz não tem o que oferecer: vai direto para as pastas.
    const hasRootMenu = showMarkUnread && !alreadyUnread
    const [view, setView] = useState<'root' | 'folders'>(
        hasRootMenu ? 'root' : 'folders',
    )

    const close = () => {
        // Remove o foco para fechar o dropdown do DaisyUI
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
        }
        setCreating(false)
        if (hasRootMenu) setView('root')
    }

    const handleMarkUnread = async () => {
        if (busy) return
        setBusy(true)
        try {
            const res = await markChatUnread(chatId)
            if (res.success) {
                toast.success('Marcado como não lido')
                window.dispatchEvent(
                    new CustomEvent(CHAT_READ_CHANGED_EVENT, { detail: { chatId } }),
                )
                onChanged?.()
            } else {
                toast.error(res.error || 'Erro ao marcar como não lido')
            }
        } finally {
            setBusy(false)
            close()
        }
    }

    const handleMove = async (folderId: number) => {
        if (busy) return
        setBusy(true)
        try {
            const res = await moveChatToFolder({ chatId, folderId })
            if (res.success) {
                toast.success('Atendimento movido para a pasta')
                onChanged?.()
            } else {
                toast.error(res.error || 'Erro ao mover atendimento')
            }
        } finally {
            setBusy(false)
            close()
        }
    }

    const handleRemove = async () => {
        if (busy) return
        setBusy(true)
        try {
            const res = await removeChatFromFolder(chatId)
            if (res.success) {
                toast.success('Atendimento removido da pasta')
                onChanged?.()
            } else {
                toast.error(res.error || 'Erro ao remover da pasta')
            }
        } finally {
            setBusy(false)
            close()
        }
    }

    const handleCreate = async () => {
        const name = newName.trim()
        if (!name || busy) return
        setBusy(true)
        try {
            const res = await createChatFolder({ name })
            if (res.success && res.result?.id) {
                await moveChatToFolder({ chatId, folderId: res.result.id })
                toast.success(`Atendimento movido para "${name}"`)
                setNewName('')
                setCreating(false)
                onChanged?.()
            } else {
                toast.error(res.error || 'Erro ao criar pasta')
            }
        } finally {
            setBusy(false)
            close()
        }
    }

    const folderList = (
        <>
            {hasRootMenu ? (
                <li>
                    <button
                        type="button"
                        onClick={() => setView('root')}
                        className="text-xs text-base-content/60"
                    >
                        <MdArrowBack className="text-base" />
                        Voltar
                    </button>
                </li>
            ) : (
                <li className="menu-title text-xs">Mover para pasta</li>
            )}

            {folders.length === 0 && !creating && (
                <li>
                    <span className="text-xs text-base-content/50">
                        Nenhuma pasta ainda
                    </span>
                </li>
            )}

            {folders.map(folder => (
                <li key={folder.id}>
                    <button
                        type="button"
                        onClick={() => handleMove(folder.id)}
                        disabled={busy}
                        className="flex items-center justify-between"
                    >
                        <span className="flex items-center gap-2 truncate">
                            {folder.color && (
                                <span
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: folder.color }}
                                />
                            )}
                            <span className="truncate">{folder.name}</span>
                        </span>
                        {currentFolderId === folder.id && (
                            <MdCheck className="text-secondary flex-shrink-0" />
                        )}
                    </button>
                </li>
            ))}

            {currentFolderId != null && (
                <li>
                    <button
                        type="button"
                        onClick={handleRemove}
                        disabled={busy}
                        className="text-error"
                    >
                        <MdFolderOff className="text-base" />
                        Remover da pasta
                    </button>
                </li>
            )}

            <div className="divider my-1" />

            {creating ? (
                <li className="p-1">
                    <div className="flex flex-col gap-2 hover:bg-transparent">
                        <input
                            type="text"
                            autoFocus
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleCreate()
                                if (e.key === 'Escape') setCreating(false)
                            }}
                            placeholder="Nome da pasta"
                            className="input input-bordered input-xs w-full"
                            maxLength={60}
                        />
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={busy || !newName.trim()}
                                className="btn btn-secondary btn-xs flex-1"
                            >
                                Criar e mover
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setCreating(false)
                                    setNewName('')
                                }}
                                className="btn btn-ghost btn-xs"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </li>
            ) : (
                <li>
                    <button
                        type="button"
                        onClick={() => setCreating(true)}
                        disabled={busy}
                    >
                        <MdCreateNewFolder className="text-base" />
                        Nova pasta
                    </button>
                </li>
            )}
        </>
    )

    return (
        <div
            className="dropdown dropdown-end"
            onClick={e => e.stopPropagation()}
        >
            <button
                tabIndex={0}
                type="button"
                className={buttonClassName}
                title={showMarkUnread ? 'Ações da conversa' : 'Mover para pasta'}
            >
                {showMarkUnread ? (
                    <MdExpandMore className={iconClassName} />
                ) : (
                    <MdDriveFileMoveOutline className={iconClassName} />
                )}
            </button>
            <ul
                tabIndex={0}
                className="dropdown-content menu menu-sm z-50 mt-1 w-56 rounded-box bg-base-100 p-2 shadow-lg border border-base-300"
            >
                {hasRootMenu && view === 'root' ? (
                    <>
                        <li>
                            <button
                                type="button"
                                onClick={() => setView('folders')}
                                disabled={busy}
                            >
                                <MdDriveFileMoveOutline className="text-base" />
                                Mover para pasta
                            </button>
                        </li>
                        <li>
                            <button
                                type="button"
                                onClick={handleMarkUnread}
                                disabled={busy}
                            >
                                <MdMarkChatUnread className="text-base" />
                                Marcar como não lido
                            </button>
                        </li>
                    </>
                ) : (
                    folderList
                )}
            </ul>
        </div>
    )
}
